import NoData from '@root/components/common/NoData';
import MuiTable, { DEFAULT_PAGE_SIZE, DEFAULT_ROWS_PER_PAGE_OPTIONS } from '@root/components/table/table';
// import { VulnerabilityCounts } from './VulnerabilityCounts';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import agent from '@root/agent';
import useSortData from '@root/hooks/useSortData';
import HeaderBarNew from '@root/components/HeaderBarNew';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  // useEffect
} from 'react';
import { CustomCursorFooter } from '@root/components/table/CustomCursorFooter';
import { useSelectedEnvironments } from '@root/hooks/environments';
import { k8workloadColumns } from './workloadColumns';
// import TableSkeleton from '@root/components/table/skeleton';
import SideOverlay from '../Sideoverlay';

import PaginatedFilter from '@root/components/FilterNew/PaginatedFilter';
import { toast } from 'sonner';
import { exportPipeline } from '../exportPipeline';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import { useQueryClient } from '@tanstack/react-query';

const EXPORTED_COLS = [
  'account_id',
  'cluster',
  'namespace',
  'workload_name',
  'workload_kind',
  'is_crown_jewel',
  'vulnerability_counts',
  'image_count',
  'status',
  'last_scan',
];

const EXPORTED_HEADERNAME = [
  'Account ID',
  'Cluster',
  'Namespace',
  'Workload Name',
  'Workload Kind',
  'Crown Tagged',
  'Vulnerabilities',
  'Image',
  'State',
  'Last Scan',
];

const Workload = ({ isEnabled }) => {
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(0); // Start from 0 for cursor pagination
  const [cursors, setCursors] = useState(['']); // Stack of cursors, empty string for first page
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedSearchString = useDebouncedValue(searchString, 750);
  const { sortData, handleSortModelChange } = useSortData();
  const { selectedEnvironments } = useSelectedEnvironments();
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isFetchingTagCrownJewel, setIsFetchingTagCrownJewel] = useState(false);
  const queryClient = useQueryClient();
  // const [filterData, setFilterData] = useState(null);
  // const [isFetchingFilterData, setIsFetchingFilterData] = useState(false);
  const { data: sbomData, isFetching: isFetchingSbomData } = useEnhancedQuery(
    [
      'get_workload_table_data',
      debouncedSearchString,
      selectedEnvironments.map(item => item.id),
      currentPage, // Added to trigger refetch when page changes
      cursors[currentPage],
      pageSize,
      sortData,
      isEnabled,
      selectedFilters,
    ],
    () =>
      agent.Kubernetes.get_workload_table_data({
        environments: selectedEnvironments.map(item => item.id),

        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        workload: [],
        cves: [],
        images: [],
        sbom: [],
        clusters: selectedFilters?.clusters || [],
        risk_spotlight: isEnabled,
        search_query: debouncedSearchString ? debouncedSearchString : '',
        cursor: currentPage === 0 ? null : cursors[currentPage], // First page has no cursor
        page_number: currentPage + 1,
        rows_per_page: pageSize,
        namespace: selectedFilters?.namespace || [],
        kind: selectedFilters?.workload_kind || [],
        is_crown_jewel: selectedFilters?.crown_jewel?.length === 1 ? selectedFilters?.crown_jewel[0] : null,
        name: selectedFilters?.workload_name || [],
        pod: selectedFilters?.pod || [],
        container_name: selectedFilters.container_name || [],
        export: false,
      }),
  );

  useEffect(() => {
    if (isFetchingSbomData) return;
    if (sbomData?.pagination) {
      const { has_next, next_cursor } = sbomData.pagination;
      setHasNextPage(has_next);

      // Store next cursor if we have one and don't already have it
      if (has_next && next_cursor) {
        setCursors(prev => {
          const nextIndex = currentPage + 1;
          // Only update if we don't have this cursor yet or if it's different
          if (!prev[nextIndex] || prev[nextIndex] !== next_cursor) {
            const newCursors = [...prev];
            newCursors[nextIndex] = next_cursor;
            return newCursors;
          }
          return prev;
        });
      }
    }
  }, [sbomData, currentPage, isFetchingSbomData]);

  // Reset pagination when filters/sort/search changes
  useEffect(() => {
    setCurrentPage(0);
    setCursors([null]); // First page has no cursor
  }, [debouncedSearchString, selectedFilters, sortData, selectedEnvironments, isEnabled]);

  const handleNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handlePageSizeChange = useCallback(_pageSize => {
    setPageSize(_pageSize);
    setCurrentPage(0);
    setCursors(['']);
    setHasNextPage(false);
  }, []);

  const fetchKubernetesVulWorkloadFilterPropertyValues = useCallback(
    async paginatedFilter => {
      let response = await agent.Kubernetes.get_workload_filter_data({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
        search_key: paginatedFilter ? paginatedFilter?.search_key : '',
        filter_particular_key: paginatedFilter
          ? paginatedFilter?.filter_particular_key?.toLowerCase().replace(/\s+/g, '_')
          : '',
        previous_filter: [],
        page_no: paginatedFilter ? paginatedFilter?.page_no : 1,
        row_per_page: paginatedFilter ? paginatedFilter?.row_per_page : 20,
      });

      if (paginatedFilter?.filter_particular_key === 'Crown Jewel') {
        response = {
          data: {
            crown_jewel: {
              data: [true, false],
              num_records: 2,
              has_more: false,
            },
          },
          error: 0,
        };
      }

      return response;
    },
    [selectedEnvironments],
  );

  const propertyValues = useMemo(() => {
    // if (isFetchingFilterData) {
    return {
      Clusters: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      Namespace: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      'Workload Kind': {
        data: [],
        num_records: 0,
        has_more: false,
      },
      'Workload Name': {
        data: [],
        num_records: 0,
        has_more: false,
      },
      Pod: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      'Container Name': {
        data: [],
        num_records: 0,
        has_more: false,
      },
      'Crown Jewel': {
        data: ['True', 'False'],
        num_records: 0,
        has_more: false,
      },
    };
  }, []);

  const _tableData = useMemo(() => {
    if (isFetchingSbomData || !sbomData?.data) return [];
    return sbomData?.data?.map((v, i) => ({ ...v, id: i })) ?? [];
  }, [isFetchingSbomData, sbomData?.data]);

  const handleRowClick = useCallback(r => {
    setSelectedRow(r.row);
    setShowDetailsPanel(true);
  }, []);
  const handleCloseDetailsPanel = useCallback(() => {
    setShowDetailsPanel(false);
    setSelectedRow(null);
  }, []);

  const handleTagCrownJewel = useCallback(
    async (params, isChecked) => {
      try {
        setIsFetchingTagCrownJewel(true);
        let response = null;
        const { account_id, cluster, namespace, workload_name, workload_kind } = params;
        if (isChecked) {
          response = await agent.Kubernetes.get_tag_crown_jewel({
            account_id,
            cluster,
            namespace,
            workload_name,
            workload_kind,
            notes: '',
          });
        } else {
          response = await agent.Kubernetes.get_untag_crown_jewel({
            account_id,
            cluster,
            namespace,
            workload_name,
            workload_kind,
            notes: '',
          });
        }
        queryClient.invalidateQueries({ queryKey: ['get_workload_table_data'] });
        console.log('response', response);
      } catch (err) {
        console.log(err);
      } finally {
        setIsFetchingTagCrownJewel(false);
      }
    },
    [queryClient],
  );

  const handleExportData = useCallback(async () => {
    toast.success('Exporting data...');

    try {
      const response = await agent.Kubernetes.get_workload_table_data({
        // scope: selectedEnvironmentTags,
        environments: selectedEnvironments.map(item => item.id),

        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        workload: [],
        cves: [],
        images: [],
        sbom: [],
        clusters: selectedFilters?.clusters || [],
        risk_spotlight: isEnabled,
        search_query: debouncedSearchString ? debouncedSearchString : '',
        page_number: currentPage,
        rows_per_page: pageSize,
        is_crown_jewel: selectedFilters?.crown_jewel?.length === 1 ? selectedFilters?.crown_jewel[0] : null,
        namespace: selectedFilters?.namespace || [],
        kind: selectedFilters?.kind || [],
        export: true,
        name: selectedFilters?.name || [],
        pod: selectedFilters?.pod || [],
        container_name: selectedFilters.container_name || [],
      });

      if (response?.data) {
        const data = response.data?.map(item => {
          const _item = {};
          for (const key of EXPORTED_COLS) {
            if (item[key]) {
              _item[key] = item[key];
            } else {
              _item[key] = '-';
            }
          }
          const lastScanFormat = DateFormatPipe(_item['last_scan'], true);
          return {
            ..._item,
            vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
            last_scan: lastScanFormat,
          };
        });

        exportPipeline(data, EXPORTED_COLS, EXPORTED_HEADERNAME, 'Kubernetes Workloads');
      }

      toast.success('Data exported successfully.');
    } catch (err) {
      console.log(err);
      toast.error(`Failed to export data. ${err.message}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedEnvironments,
    sortData,
    selectedFilters?.clusters,
    selectedFilters?.severity,
    selectedFilters?.fixable,
    selectedFilters.cvss_attack_vector,
    selectedFilters.epss_score,
    selectedFilters?.exploitable,
    selectedFilters?.cve_id,
    debouncedSearchString,
    currentPage,
    pageSize,
    isEnabled,
  ]);

  return (
    <div style={{ margin: '15px' }}>
      <HeaderBarNew
        // marginTop={'5px'}
        marginBottom={'13px'}
        title={''}
        searchBar
        searchBarValue={searchString}
        setOpenFilter={setOpenFilter}
        openFilter={openFilter}
        exportButton={true}
        disableExportMenu
        setSearchTerm={setSearchString}
        onExportButtonClick={handleExportData}
        searchBarOnChange={e => setSearchString(e.target.value)}
      />{' '}
      <PaginatedFilter
        openFilter={openFilter}
        option={propertyValues}
        setSelectedFilters={setSelectedFilters}
        selectedFilters={selectedFilters}
        fetchFilterPropertyValues={fetchKubernetesVulWorkloadFilterPropertyValues}
        defaultValue={{}}
      />
      <MuiTable
        columns={k8workloadColumns(isFetchingTagCrownJewel, handleTagCrownJewel)}
        paginationMode={'server'}
        rows={_tableData}
        LoaderState={isFetchingSbomData}
        components={{
          NoRowsOverlay: () => <NoData message={'No Workload present!'} />,
          Footer: () => (
            <CustomCursorFooter
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              onNextPage={handleNextPage}
              onPrevPage={handlePrevPage}
              loading={isFetchingSbomData}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            />
          ),
        }}
        sortingMode="server"
        handleSortModelChange={handleSortModelChange}
        total_size={-1}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        noRowsMessage={'No Workload present!'}
        rowHeight={49.9}
        isDynamicHeight={true}
        onRowClick={handleRowClick}
        cursorPagination={true}
        currentPage={currentPage}
        hasNextPage={hasNextPage}
        onNextPage={handleNextPage}
        onPrevPage={handlePrevPage}
      />
      {showDetailsPanel && (
        <SideOverlay
          key={showDetailsPanel.toString()}
          onClose={handleCloseDetailsPanel}
          selectedRow={selectedRow}
          type={'workloads'}
        />
      )}
    </div>
  );
};
export default Workload;
